import React, { useState } from "react";
import API from "../../API/newIndex";
import { XLg } from "react-bootstrap-icons";
import {
  ButtonComponent,
  InputComponent,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import TextareaComponent from "../FormInput/TextareaComponent";
import { clientData as globalClientData } from "../../controllers/GetClient";
import { ConvertCurrency, GetCurrencySymbol } from "../../utils/priceFormatter";

const getPayoutDetails = (payout) => {
  const details = payout?.vendor?.payout_account_details || payout?.payout_account_details || {};
  const method = (payout?.payment_method || "").toLowerCase();

  if (method.includes("upi")) {
    return {
      methodName: "UPI",
      detailsList: [
        { label: "UPI ID", value: details.upi_id || details.upi },
        { label: "Phone", value: details.phone },
      ].filter((item) => item.value),
    };
  }

  if (method.includes("paypal")) {
    return {
      methodName: "PayPal",
      detailsList: [
        { label: "PayPal Email", value: details.paypal_email || details.email },
      ].filter((item) => item.value),
    };
  }

  if (method.includes("bank")) {
    return {
      methodName: "Bank Transfer",
      detailsList: [
        { label: "Bank Name", value: details.bank_name },
        { label: "Account No.", value: details.account_number },
        { label: "IFSC Code", value: details.ifsc_code },
        { label: "Holder Name", value: details.account_holder_name || details.account_name },
      ].filter((item) => item.value),
    };
  }

  const fallbackList = [];
  if (details.upi_id || details.upi) fallbackList.push({ label: "UPI ID", value: details.upi_id || details.upi });
  if (details.bank_name || details.account_number) {
    if (details.bank_name) fallbackList.push({ label: "Bank", value: details.bank_name });
    if (details.account_number) fallbackList.push({ label: "Account No.", value: details.account_number });
    if (details.ifsc_code) fallbackList.push({ label: "IFSC Code", value: details.ifsc_code });
  }
  if (details.paypal_email) fallbackList.push({ label: "PayPal Email", value: details.paypal_email });

  return {
    methodName: payout?.payment_method || "Bank Transfer",
    detailsList: fallbackList,
  };
};

const ProcessPayoutModal = ({ payout, onClose, onSuccess }) => {
  const [status, setStatus] = useState("paid");
  const [paymentMethod, setPaymentMethod] = useState(payout?.payment_method || "Bank Transfer");
  const [transactionId, setTransactionId] = useState(payout?.transaction_id || "");
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await API({
        url: "/admin/vendor/payout/process",
        method: "post",
        data: {
          payout_id: payout._id,
          status,
          payment_method: paymentMethod,
          transaction_id: transactionId,
          rejection_reason: status === "rejected" ? rejectionReason : undefined,
        },
      });
      onSuccess();
    } catch (err) {
      console.error("Process payout error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to process payout.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="secondary-bg-color w-full max-w-md rounded-[12px] shadow-2xl overflow-hidden border main-border-color animate-fadeIn text-[var(--primary-text-color)]">
        <div className="flex justify-between items-center px-6 py-4 border-b main-border-color">
          <h2 className="text-lg font-bold primary-text-color">
            Process Withdrawal Request
          </h2>
          <ButtonComponent
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="!p-1.5 table-text hover:primary-text-color hover:bg-[var(--focus-tab-color)]"
          >
            <XLg size={16} />
          </ButtonComponent>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-sm">
          {error && (
            <div className="p-3 text-xs bg-rose-50 text-rose-600 rounded-lg border border-rose-200 font-medium">
              {error}
            </div>
          )}

          <div className="p-4 rounded-lg bg-[var(--primary-bg-color)] border main-border-color space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs table-text">Vendor</div>
                <div className="font-semibold primary-text-color">
                  {payout?.vendor?.shop_name || payout?.vendor?.name}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs table-text">Amount</div>
                <div className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                  {GetCurrencySymbol(globalClientData)}
                  {Number(ConvertCurrency(payout?.amount || 0, globalClientData)).toFixed(globalClientData?.number_of_decimals ?? 2)}
                </div>
              </div>
            </div>

            {/* Render payout destination account details according to method */}
            {(() => {
              const { methodName, detailsList } = getPayoutDetails(payout);
              return (
                <div className="pt-2 border-t main-border-color text-xs">
                  <div className="font-semibold primary-text-color capitalize mb-1">
                    Payout Destination ({methodName})
                  </div>
                  {detailsList.length > 0 ? (
                    <div className="grid grid-cols-1 gap-1 text-[11px]">
                      {detailsList.map((item, idx) => (
                        <div key={idx} className="flex justify-between table-text">
                          <span className="font-medium">{item.label}:</span>
                          <span className="font-semibold primary-text-color font-mono">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[11px] table-text italic">No account details provided</div>
                  )}
                </div>
              );
            })()}
          </div>

          <SelectComponent
            label="Action Status"
            required={true}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: "paid", label: "Mark as Paid" },
              { value: "approved", label: "Approve Request" },
              { value: "rejected", label: "Reject Request" },
            ].filter((opt) => opt.value !== (payout?.status || "").toLowerCase())}
          />

          {status === "paid" && (
            <>
              <InputComponent
                label="Payment Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="Bank Transfer, UPI, Razorpay..."
                disabled
              />
              <InputComponent
                label="Transaction / Reference ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="TXN987654321"
              />
            </>
          )}

          {status === "rejected" && (
            <TextareaComponent
              label="Rejection Reason"
              rows={2}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejecting payout..."
            />
          )}

          <div className="flex justify-end gap-3 pt-3 border-t main-border-color">
            <ButtonComponent variant="outline" size="md" onClick={onClose}>
              Cancel
            </ButtonComponent>
            <ButtonComponent type="submit" variant="primary" size="md" loading={submitting}>
              Save Action
            </ButtonComponent>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcessPayoutModal;
